import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Banknote, CalendarClock, Check, ChevronRight, Landmark, Loader2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { cn, formatBRL } from "@/lib/utils";
import { panelAccentDot, panelControl, panelFieldLabel, panelPill } from "@/lib/panel-ui";

function addBusinessDays(from: Date, days: number) {
  const date = new Date(from);
  let remaining = days;
  while (remaining > 0) {
    date.setDate(date.getDate() + 1);
    if (date.getDay() !== 0 && date.getDay() !== 6) remaining--;
  }
  return date;
}

function parseWithdrawalAmount(value: string) {
  const normalized = value.replace(/\./g, "").replace(",", ".").replace(/[^\d.-]/g, "");
  return Number(normalized) || 0;
}

function formatWithdrawalAmount(value: number) {
  return value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface BalanceItem {
  event_id: string;
  event_title: string;
  available: number;
}

interface BankAccountItem {
  id: string;
  account_name: string;
  bank_name: string | null;
  account_number: string | null;
  is_default?: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  balances: BalanceItem[];
  bankAccounts: BankAccountItem[];
  preselectedEventId?: string;
  onSubmit: (params: { eventId: string; amount: number; bankAccountId: string }) => Promise<void>;
  isSubmitting: boolean;
  weeklyBlock: { unlocksAt: Date; requestedAt: Date } | null;
}

export function WithdrawalRequestModal({
  open,
  onOpenChange,
  balances,
  bankAccounts,
  preselectedEventId,
  onSubmit,
  isSubmitting,
  weeklyBlock,
}: Props) {
  const { t } = useTranslation("producerFinancial");
  
  // Eventos com saldo disponível > 0
  const eventsWithBalance = useMemo(
    () => balances.filter((b) => Number(b.available) > 0),
    [balances]
  );

  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [withdrawAmount, setWithdrawAmount] = useState<string>("");
  const [selectedBankId, setSelectedBankId] = useState<string>("");

  // Inicializar seleção do evento quando abre
  useEffect(() => {
    if (!open) return;
    if (preselectedEventId && preselectedEventId !== "all") {
      setSelectedEventId(preselectedEventId);
    } else if (eventsWithBalance.length > 0) {
      setSelectedEventId(eventsWithBalance[0].event_id);
    } else if (balances.length > 0) {
      setSelectedEventId(balances[0].event_id);
    }
  }, [open, preselectedEventId, eventsWithBalance, balances]);

  // Inicializar conta bancária padrão
  useEffect(() => {
    if (!selectedBankId && bankAccounts.length) {
      setSelectedBankId(
        (bankAccounts.find((account) => account.is_default) || bankAccounts[0]).id
      );
    }
  }, [bankAccounts, selectedBankId]);

  const selectedEvent = useMemo(
    () => balances.find((b) => b.event_id === selectedEventId),
    [balances, selectedEventId]
  );

  const maxAvailable = Number(selectedEvent?.available || 0);
  const numericAmount = parseWithdrawalAmount(withdrawAmount);
  const amountTooHigh = numericAmount > maxAvailable;
  const expectedPayment = useMemo(() => addBusinessDays(new Date(), 2), []);

  const canSubmit = Boolean(
    selectedEvent &&
    numericAmount > 0 &&
    !amountTooHigh &&
    selectedBankId &&
    !weeklyBlock &&
    !isSubmitting
  );

  const handlePercentage = (percentage: number) => {
    if (!maxAvailable) return;
    const val = (maxAvailable * percentage) / 100;
    setWithdrawAmount(formatWithdrawalAmount(val));
  };

  const handleClose = () => {
    setWithdrawAmount("");
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    if (!canSubmit) return;
    await onSubmit({
      eventId: selectedEventId,
      amount: numericAmount,
      bankAccountId: selectedBankId,
    });
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg sm:max-w-xl rounded-[28px] border border-border/70 bg-white dark:bg-[#18181b] p-0 overflow-hidden shadow-2xl">
        {/* Header Apple Sheet */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-border/50">
          <div className="flex items-center gap-2">
            <span className={panelAccentDot} />
            <DialogTitle className="font-display text-base sm:text-lg font-semibold tracking-tight text-foreground">
              Solicitar saque
            </DialogTitle>
          </div>
          <DialogDescription className="mt-1 text-xs text-muted-foreground">
            O saque é realizado por evento e transferido para sua conta cadastrada.
          </DialogDescription>
        </div>

        {/* Formulário */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 max-h-[calc(85vh-130px)] overflow-y-auto">
          {weeklyBlock && (
            <div className="flex items-start gap-3 rounded-2xl bg-amber-500/10 border border-amber-500/20 p-3.5 sm:p-4">
              <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                Você já solicitou um saque recentemente. Próximo saque disponível em{" "}
                <span className="font-semibold">{format(weeklyBlock.unlocksAt, "dd/MM/yyyy")}</span>.
              </p>
            </div>
          )}

          {/* 1. Selecionar o Evento */}
          <div>
            <label className={panelFieldLabel}>De qual evento você quer sacar?</label>
            {eventsWithBalance.length === 0 ? (
              <div className="mt-1.5 rounded-2xl bg-secondary/50 p-4 text-xs text-muted-foreground">
                Nenhum evento possui saldo liberado no momento. Aguarde a compensação das vendas.
              </div>
            ) : (
              <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                <SelectTrigger className={cn(panelControl, "mt-1.5 h-12")}>
                  <SelectValue placeholder="Selecione o evento" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {balances.map((event) => {
                    const avail = Number(event.available || 0);
                    return (
                      <SelectItem
                        key={event.event_id}
                        value={event.event_id}
                        disabled={avail <= 0}
                      >
                        <div className="flex items-center justify-between gap-3 w-full py-0.5">
                          <span className="font-medium truncate max-w-[280px]">
                            {event.event_title}
                          </span>
                          <span
                            className={cn(
                              "text-xs tabular-nums font-semibold shrink-0",
                              avail > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"
                            )}
                          >
                            {formatBRL(avail)}
                          </span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}

            {selectedEvent && (
              <p className="mt-1.5 text-xs text-muted-foreground flex items-center justify-between">
                <span>Saldo liberado para transferência:</span>
                <span className="font-semibold text-foreground tabular-nums">
                  {formatBRL(maxAvailable)}
                </span>
              </p>
            )}
          </div>

          {/* 2. Valor do Saque */}
          {selectedEvent && maxAvailable > 0 && (
            <div>
              <label className={panelFieldLabel} htmlFor="modal-withdrawal-amount">
                Quanto você quer sacar?
              </label>
              <div className="relative mt-1.5">
                <span className="absolute left-4 top-1/2 z-10 -translate-y-1/2 text-sm font-semibold text-muted-foreground">
                  R$
                </span>
                <Input
                  id="modal-withdrawal-amount"
                  inputMode="decimal"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value.replace(/[^\d.,]/g, ""))}
                  onBlur={() => numericAmount > 0 && setWithdrawAmount(formatWithdrawalAmount(numericAmount))}
                  placeholder="0,00"
                  className={cn(panelControl, "h-12 pl-12 text-base sm:text-lg font-semibold tabular-nums")}
                />
              </div>

              {amountTooHigh && (
                <p className="mt-1.5 text-xs text-destructive">
                  O valor supera o saldo disponível de {formatBRL(maxAvailable)}.
                </p>
              )}

              <div className="mt-2.5 grid grid-cols-4 gap-1.5 sm:gap-2">
                {[25, 50, 75, 100].map((percentage) => (
                  <button
                    key={percentage}
                    type="button"
                    onClick={() => handlePercentage(percentage)}
                    className="h-11 sm:h-9 rounded-full bg-secondary/70 text-xs font-semibold text-foreground/80 hover:bg-secondary hover:text-foreground active:scale-[0.96] transition-all min-h-[44px] sm:min-h-0 flex items-center justify-center"
                  >
                    {percentage === 100 ? "Tudo" : `${percentage}%`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 3. Conta de Destino */}
          <div>
            <label className={panelFieldLabel}>Conta bancária de destino</label>
            {bankAccounts.length === 0 ? (
              <div className="mt-1.5 rounded-2xl border border-dashed border-border/70 p-4 text-center text-xs text-muted-foreground">
                <Landmark className="mx-auto mb-2 h-5 w-5 text-muted-foreground/60" />
                Nenhuma conta bancária cadastrada. Cadastre uma conta na aba "Contas Bancárias".
              </div>
            ) : (
              <Select value={selectedBankId} onValueChange={setSelectedBankId}>
                <SelectTrigger className={cn(panelControl, "mt-1.5 h-12")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      <span className="font-medium">{account.account_name}</span>
                      <span className="text-muted-foreground text-xs ml-1.5">
                        · {account.bank_name} •••• {String(account.account_number || "").slice(-4)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* 4. Previsão de Pagamento */}
          {numericAmount > 0 && !amountTooHigh && (
            <div className="rounded-2xl bg-secondary/40 border border-border/40 p-4 space-y-2">
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="text-muted-foreground">Valor a ser transferido:</span>
                <span className="font-display font-semibold text-foreground tabular-nums text-base">
                  {formatBRL(numericAmount)}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs border-t border-border/40 pt-2 text-muted-foreground">
                <span>Previsão de crédito na conta:</span>
                <span className="font-medium text-foreground">até {format(expectedPayment, "dd/MM/yyyy")} (2 dias úteis)</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer Apple Sheet */}
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-t border-border/50 bg-secondary/20 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-[11px] text-muted-foreground">
            Sem cobrança de taxas adicionais para saques.
          </p>
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-2.5 w-full sm:w-auto">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-full h-11 sm:h-10 px-4 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors w-full sm:w-auto active:scale-[0.98]"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!canSubmit}
              className={cn(
                panelPill,
                "h-12 sm:h-11 px-6 text-sm sm:text-xs font-semibold bg-primary text-primary-foreground transition-all shadow-sm hover:shadow",
                "disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 flex items-center justify-center gap-2 active:scale-[0.97] w-full sm:w-auto"
              )}
            >
              {isSubmitting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Banknote className="h-4 w-4" />
              )}
              <span>
                {numericAmount > 0 ? `Solicitar ${formatBRL(numericAmount)}` : "Solicitar saque"}
              </span>
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
